import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { toast } from 'sonner';
import { fetchFileAsArrayBuffer } from '@/utils/fileUtils';

const ROWS_PER_PAGE = 50;

// Simple CSV parser
function parseCSV(text) {
  const lines = text.split('\n');
  const result = [];

  for (let line of lines) {
    if (!line.trim()) continue;

    const row = [];
    let cell = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const nextChar = line[i + 1];

      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          // Escaped quote
          cell += '"';
          i++;
        } else {
          // Toggle quote state
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        // End of cell
        row.push(cell);
        cell = '';
      } else {
        cell += char;
      }
    }

    // Add last cell
    row.push(cell);
    result.push(row);
  }

  return result;
}

export default function CSVViewer({ open, onOpenChange, fileKey, fileName, bucket }) {
  const [data, setData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (open && fileKey) {
      loadCSVData();
    }
  }, [open, fileKey]);

  const loadCSVData = async () => {
    setLoading(true);
    try {
      // Use utility function to fetch file
      const arrayBuffer = await fetchFileAsArrayBuffer(fileKey, bucket);

      // Convert ArrayBuffer to text
      const decoder = new TextDecoder('utf-8');
      const text = decoder.decode(arrayBuffer);

      // Parse CSV
      const rows = parseCSV(text);

      if (rows.length > 0) {
        // First row as headers
        const headers = rows[0].map((h, idx) => h || `Column ${idx + 1}`);
        setColumns(headers);

        // Rest as data rows
        const dataRows = rows.slice(1).map((row) => {
          const rowObj = {};
          headers.forEach((header, idx) => {
            rowObj[header] = row[idx] != null ? row[idx] : '';
          });
          return rowObj;
        });

        setData(dataRows);
      } else {
        setColumns([]);
        setData([]);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error loading CSV file:', error);
      toast.error(`Failed to load CSV file: ${error.message}`);
      setLoading(false);
    }
  };

  // Filter data based on search query
  const filteredData = searchQuery
    ? data.filter((row) =>
        Object.values(row).some((value) =>
          String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
      )
    : data;

  // Paginate the filtered data
  const totalPages = Math.ceil(filteredData.length / ROWS_PER_PAGE);
  const startIndex = (currentPage - 1) * ROWS_PER_PAGE;
  const endIndex = startIndex + ROWS_PER_PAGE;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to first page on search
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="!max-w-[95vw] w-[95vw] h-[90vh] overflow-hidden flex flex-col p-6">
        <DialogHeader>
          <DialogTitle>View CSV File: {fileName}</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="space-y-3 flex-1 overflow-auto">
            <Skeleton className="h-10 w-full" />
            {[...Array(10)].map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : (
          <>
            {/* Search Bar */}
            <div className="flex items-center gap-4 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Search in table..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="pl-10"
                />
              </div>
              <div className="text-sm text-slate-600 whitespace-nowrap">
                Showing {startIndex + 1}-{Math.min(endIndex, filteredData.length)} of{' '}
                {filteredData.length} rows
                {searchQuery && ` (filtered from ${data.length} total)`}
              </div>
            </div>

            {/* Table */}
            <div className="flex-1 overflow-auto border rounded-lg">
              {paginatedData.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  {searchQuery ? 'No rows match your search' : 'No data available'}
                </div>
              ) : (
                <Table>
                  <TableHeader className="sticky top-0 bg-card-bg z-10">
                    <TableRow>
                      {columns.map((column, idx) => (
                        <TableHead key={idx} className="font-semibold">
                          {column}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedData.map((row, rowIndex) => (
                      <TableRow key={rowIndex}>
                        {columns.map((column, colIdx) => (
                          <TableCell key={colIdx} className="whitespace-nowrap">
                            {row[column] != null ? String(row[column]) : ''}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between pt-4 border-t">
                <div className="text-sm text-slate-600">
                  Page {currentPage} of {totalPages}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
